import styled from '@emotion/styled';

const cardShadow = '0 24px 60px rgba(21, 41, 77, 0.08)';
const lineColor = '#c7d2e4';
const accentColor = '#204a87';

export const OrganizationContainer = styled.article`
	max-width: 124rem;
	width: 100%;
	margin: 0 auto;
	padding: 8rem 2rem 10rem;
	display: flex;
	flex-direction: column;
	gap: 4rem;
`;

export const OrganizationHeader = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	gap: 1.6rem;
`;

export const Badge = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0.8rem 1.4rem;
	border-radius: 999px;
	background: rgba(32, 74, 135, 0.08);
	color: ${accentColor};
	font-size: ${(props) => props.theme.fontSize.sizeSm};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	letter-spacing: 0.08em;
	text-transform: uppercase;
`;

export const Title = styled.h2`
	font-size: ${(props) => props.theme.fontSize.size5xl};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: 1.2;
	letter-spacing: -0.04em;
	color: #1d2a3a;

	@media screen and (max-width: 900px) {
		font-size: 3rem;
	}
`;

export const Description = styled.p`
	max-width: 68rem;
	font-size: ${(props) => props.theme.fontSize.sizeLg};
	line-height: 1.6;
	letter-spacing: -0.03em;
	color: #52606d;
`;

export const ChartCard = styled.section`
	position: relative;
	padding: 4.8rem 4rem;
	border-radius: 3.2rem;
	background: radial-gradient(circle at top left, rgba(78, 121, 182, 0.12), transparent 24%), linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
	box-shadow: ${cardShadow};
	border: 1px solid rgba(32, 74, 135, 0.08);
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 2.4rem;
	overflow: hidden;

	&::before,
	&::after {
		content: '';
		position: absolute;
		border-radius: 50%;
		background: rgba(32, 74, 135, 0.05);
		pointer-events: none;
	}

	&::before {
		width: 26rem;
		height: 26rem;
		top: -12rem;
		right: -10rem;
	}

	&::after {
		width: 20rem;
		height: 20rem;
		bottom: -10rem;
		left: -8rem;
	}

	@media screen and (max-width: 900px) {
		padding: 3.2rem 2rem;
		border-radius: 2.4rem;
	}
`;

export const BaseNode = styled.div`
	position: relative;
	z-index: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	border-radius: 2rem;
	border: 1px solid rgba(32, 74, 135, 0.16);
	background: rgba(255, 255, 255, 0.94);
	box-shadow: 0 16px 32px rgba(31, 52, 89, 0.08);
	color: #1d2a3a;
`;

export const TopNode = styled(BaseNode)`
	min-height: 11.6rem;
	width: min(100%, 38rem);
	padding: 2.2rem 2.8rem;
	background: linear-gradient(135deg, #204a87 0%, #2a5aa4 100%);
	border: none;
	box-shadow: 0 22px 36px rgba(32, 74, 135, 0.26);
	color: #ffffff;
`;

export const NodeEyebrow = styled.span`
	font-size: ${(props) => props.theme.fontSize.sizeSm};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	letter-spacing: 0.08em;
	text-transform: uppercase;
	opacity: 0.86;
`;

export const NodeTitle = styled.strong`
	font-size: ${(props) => props.theme.fontSize.size2xl};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: 1.4;
	letter-spacing: -0.03em;

	@media screen and (max-width: 900px) {
		font-size: ${(props) => props.theme.fontSize.sizeXl};
	}
`;

export const VerticalConnector = styled.div`
	position: relative;
	z-index: 0;
	width: 0.2rem;
	height: 3.6rem;
	background: linear-gradient(180deg, ${accentColor} 0%, ${lineColor} 100%);

	@media screen and (max-width: 900px) {
		height: 2.4rem;
	}
`;

export const ExecutiveRow = styled.div`
	position: relative;
	z-index: 1;
	display: grid;
	grid-template-columns: repeat(2, minmax(18rem, 22rem));
	gap: 2.4rem 8rem;

	&::before {
		content: '';
		position: absolute;
		top: 50%;
		left: calc(50% - 4rem);
		right: calc(50% - 4rem);
		height: 0.2rem;
		background: ${lineColor};
		transform: translateY(-50%);
	}

	@media screen and (max-width: 900px) {
		grid-template-columns: 1fr;
		width: 100%;
		max-width: 28rem;
		gap: 1.6rem;

		&::before {
			display: none;
		}
	}
`;

export const SmallNode = styled(BaseNode)`
	min-height: 7.6rem;
	padding: 1.6rem 2rem;
	font-size: ${(props) => props.theme.fontSize.size2xl};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
`;

export const MidNode = styled(BaseNode)`
	min-height: 8.8rem;
	width: min(100%, 32rem);
	padding: 1.8rem 2rem;
	font-size: ${(props) => props.theme.fontSize.size2xl};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
`;

export const CommitteeSection = styled.div`
	position: relative;
	z-index: 1;
	width: 100%;
	display: grid;
	grid-template-columns: minmax(0, 1fr) 28rem;
	gap: 3.2rem;
	align-items: start;

	@media screen and (max-width: 1100px) {
		grid-template-columns: 1fr;
	}
`;

export const CommitteeColumn = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 2.4rem;
`;

export const CommitteeRow = styled.div`
	position: relative;
	width: 100%;
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 1.6rem;

	&::before {
		content: '';
		position: absolute;
		top: -1.8rem;
		left: 12.5%;
		right: 12.5%;
		height: 0.2rem;
		background: ${lineColor};
	}

	&::after {
		content: '';
		position: absolute;
		top: -1.8rem;
		left: 50%;
		width: 0.2rem;
		height: 1.8rem;
		background: ${lineColor};
		transform: translateX(-50%);
	}

	@media screen and (max-width: 900px) {
		grid-template-columns: 1fr;
		gap: 1.2rem;

		&::before,
		&::after {
			display: none;
		}
	}
`;

export const CommitteeNode = styled(BaseNode)<{ isAccent?: boolean }>`
	min-height: 9.4rem;
	padding: 1.8rem 1.2rem;
	font-size: ${(props) => props.theme.fontSize.sizeXl};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	background: ${(props) =>
		props.isAccent ? 'linear-gradient(135deg, rgba(32, 74, 135, 0.1) 0%, rgba(77, 120, 181, 0.14) 100%)' : 'rgba(255, 255, 255, 0.94)'};
	border-color: ${(props) => (props.isAccent ? 'rgba(32, 74, 135, 0.24)' : 'rgba(32, 74, 135, 0.16)')};
`;

export const OfficeNode = styled(BaseNode)`
	width: min(100%, 42rem);
	min-height: 11.6rem;
	padding: 2.4rem;
	background: linear-gradient(180deg, #ffffff 0%, #f4f8fc 100%);
`;

export const OfficeDescription = styled.p`
	margin-top: 0.8rem;
	font-size: ${(props) => props.theme.fontSize.sizeLg};
	line-height: 1.5;
	letter-spacing: -0.03em;
	color: #52606d;
`;

export const DivisionColumn = styled.div`
	position: relative;
	padding: 2.2rem 1.6rem 1.6rem;
	border-radius: 2.4rem;
	background: rgba(255, 255, 255, 0.78);
	border: 1px solid rgba(32, 74, 135, 0.12);
	box-shadow: 0 16px 30px rgba(31, 52, 89, 0.06);

	&::before {
		content: '';
		position: absolute;
		top: 4.5rem;
		left: -2rem;
		width: 2rem;
		height: 0.2rem;
		background: ${lineColor};
	}

	@media screen and (max-width: 1100px) {
		&::before {
			display: none;
		}
	}
`;

export const DivisionHeader = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.8rem;
	padding: 0 0.8rem 1.6rem;
`;

export const DivisionBadge = styled.span`
	font-size: ${(props) => props.theme.fontSize.sizeSm};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: ${accentColor};
`;

export const DivisionTitle = styled.strong`
	font-size: ${(props) => props.theme.fontSize.sizeXl};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: 1.4;
	color: #1d2a3a;
`;

export const DivisionList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

export const DivisionNode = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	text-align: center;
	min-height: 6.2rem;
	padding: 1.2rem 1.4rem;
	border-radius: 1.6rem;
	background: #ffffff;
	border: 1px solid rgba(32, 74, 135, 0.12);
	font-size: ${(props) => props.theme.fontSize.sizeLg};
	font-weight: ${(props) => props.theme.fontWeight.weightMedium};
	line-height: 1.45;
	letter-spacing: -0.03em;
	color: #28405f;
`;
